import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApiClient } from '../api/client';
import { StatCard } from '../components/ui/StatCard';
import { Card, CardTitle } from '../components/ui/Card';
import { Loading } from '../components/ui/Loading';
import {
  Users,
  FileSearch,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  TrendingUp,
} from 'lucide-react';
import { format } from 'date-fns';

const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  if (remainingSeconds === 0) {
    return `${minutes}m`;
  }
  return `${minutes}m ${remainingSeconds}s`;
};

const Dashboard: React.FC = () => {
  const { data: dashboard, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => adminApiClient.getDashboard(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return <Loading message="Loading dashboard..." />;
  }

  if (error || !dashboard) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">Failed to load dashboard data</p>
      </div>
    );
  }

  const { users, analyses, accounts } = dashboard;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-gray-400 mt-1">
            Platform overview and key metrics
          </p>
        </div>
        <div className="text-sm text-gray-500">
          Last updated: {format(new Date(dashboard.updated_at), 'MMM d, yyyy HH:mm')}
        </div>
      </div>

      {/* User Stats */}
      <section>
        <h2 className="text-lg font-semibold text-white mb-4">Users</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Users"
            value={users.total_users.toLocaleString()}
            icon={Users}
          />
          <StatCard
            title="New Today"
            value={users.new_today.toLocaleString()}
            trend={{ value: users.new_this_week, label: 'this week', isPositive: true }}
          />
          <StatCard
            title="Active Users"
            value={users.active_users.toLocaleString()}
            subtitle="Last 30 days"
          />
          <StatCard
            title="Terms Accepted"
            value={users.terms_accepted.toLocaleString()}
            subtitle={`${((users.terms_accepted / users.total_users) * 100).toFixed(1)}% of users`}
          />
        </div>
      </section>

      {/* Analysis Stats */}
      <section>
        <h2 className="text-lg font-semibold text-white mb-4">Analyses</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Analyses"
            value={analyses.total.toLocaleString()}
            icon={FileSearch}
          />
          <StatCard
            title="Today"
            value={analyses.today_count.toLocaleString()}
            trend={{ value: analyses.this_week_count, label: 'this week', isPositive: true }}
          />
          <StatCard
            title="Avg Duration"
            value={formatDuration(analyses.avg_duration_seconds)}
            icon={Clock}
          />
          <StatCard
            title="This Month"
            value={analyses.this_month_count.toLocaleString()}
            icon={TrendingUp}
          />
        </div>

        {/* Analysis Status Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <Card>
            <CardTitle className="mb-4">By Status</CardTitle>
            <div className="space-y-3">
              <StatusBar
                label="Completed"
                value={analyses.by_status.completed}
                total={analyses.total}
                color="bg-green-500"
                icon={CheckCircle}
              />
              <StatusBar
                label="Processing"
                value={analyses.by_status.processing}
                total={analyses.total}
                color="bg-blue-500"
                icon={Loader}
              />
              <StatusBar
                label="Pending"
                value={analyses.by_status.pending}
                total={analyses.total}
                color="bg-yellow-500"
                icon={Clock}
              />
              <StatusBar
                label="Failed"
                value={analyses.by_status.failed}
                total={analyses.total}
                color="bg-red-500"
                icon={XCircle}
              />
            </div>
          </Card>

          <Card>
            <CardTitle className="mb-4">By Depth</CardTitle>
            <div className="space-y-3">
              <StatusBar
                label="Quick"
                value={analyses.by_depth.quick}
                total={analyses.total}
                color="bg-gray-500"
              />
              <StatusBar
                label="Standard"
                value={analyses.by_depth.standard}
                total={analyses.total}
                color="bg-primary-500"
              />
              <StatusBar
                label="Deep"
                value={analyses.by_depth.deep}
                total={analyses.total}
                color="bg-purple-500"
              />
            </div>
          </Card>
        </div>

        {/* LLM Usage */}
        <Card className="mt-4">
          <CardTitle className="mb-4">By LLM Agent</CardTitle>
          <div className="space-y-3">
            {Object.entries(analyses.by_llm_agent).map(([agent, count]) => (
              <StatusBar
                key={agent}
                label={agent}
                value={count}
                total={analyses.total}
                color="bg-primary-500"
              />
            ))}
          </div>
        </Card>
      </section>

      {/* Account Stats */}
      <section>
        <h2 className="text-lg font-semibold text-white mb-4">Accounts</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Accounts"
            value={accounts.total.toLocaleString()}
            icon={Building2}
          />
        </div>
      </section>

    </div>
  );
};

interface StatusBarProps {
  label: string;
  value: number;
  total: number;
  color: string;
  icon?: React.ElementType;
}

const StatusBar: React.FC<StatusBarProps> = ({ label, value = 0, total, color, icon: Icon }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center">
          {Icon && <Icon className="w-4 h-4 mr-2 text-gray-400" />}
          <span className="text-sm text-gray-300">{label}</span>
        </div>
        <span className="text-sm text-gray-400">
          {value.toLocaleString()} ({percentage.toFixed(1)}%)
        </span>
      </div>
      <div className="w-full bg-gray-700 rounded-full h-2">
        <div
          className={`h-2 rounded-full ${color}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export default Dashboard;
